import React, { memo, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Dialog, { dialogClasses } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import { useSnackbar } from 'notistack';
import Button from '../../components/Button';
import GoBackButton from '../../components/BackButton';
import { Loader } from '../../components';
import { formatDate } from '../../utils/formatTime';
import { PORTAL_ROUTE } from '../../constants/routes';
import { DEVICES_PREVIOUS_PATH } from '../devices/deviceDetails/constant';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { useBulkUpdateDeviceStatus } from 'src/hooks/device';
import {
  useArchiveDashboardOfflineDevices,
  useExportDashboardClientHealthDevices,
  useInfiniteDashboardClientHealthDevices,
} from 'src/hooks/dashboard';
import { DEVICE_STATUS } from 'src/utils/enums/deviceStatus';
import { HEALTH_STATUS } from 'src/utils/enums/healthStatus';
import { objectToQueryString } from 'src/utils/uri';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${dialogClasses.paper}`]: {
    backgroundColor: '#4B4B4B',
    padding: theme.spacing(2),
    maxWidth: '800px',
    width: '100%',
    margin: theme.spacing(1),
  },
}));

const StyledDialogTitle = styled(DialogTitle)({
  fontSize: '20px',
  fontWeight: 'bold',
  textAlign: 'center',
  paddingBottom: '12px',
  paddingTop: '8px',
});

const SectionTitle = styled(Typography)(({ theme, color }) => ({
  fontSize: theme.typography.pxToRem(16),
  fontWeight: 'bold',
  color: color || '#fff',
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const ProblemDescription = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  color: '#fff',
  marginBottom: theme.spacing(1),
  lineHeight: 1.5,
}));

const SolutionText = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  color: '#fff',
  fontWeight: 500,
  marginBottom: theme.spacing(1),
}));

const DeviceItem = styled(Box)(({ theme, borderColor }) => ({
  padding: theme.spacing(1),
  backgroundColor: '#3a3a3a',
  borderRadius: theme.spacing(1),
  border: `1px solid ${borderColor}`,
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  transition: 'background-color 0.2s',
  '&:hover': {
    backgroundColor: '#4a4a4a',
  },
}));

const DeviceName = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(14),
  fontWeight: 'bold',
  color: '#fff',
}));

const DeviceInfo = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(12),
  color: '#fff',
}));

const StyledDialogActions = styled(DialogActions)({
  justifyContent: 'flex-end',
  paddingTop: '24px',
});

const ACTIVE_DEVICE_STATUSES = [DEVICE_STATUS.Approved, DEVICE_STATUS.Unknown];

const useHealthBucketDevices = ({ enabled, tagId, healthStatus }) => {
  const apiQuery = useMemo(
    () => ({
      ...(tagId ? { TagId: tagId } : {}),
      HealthStatuses: [healthStatus],
    }),
    [healthStatus, tagId],
  );

  const query = useInfiniteDashboardClientHealthDevices({
    apiQuery,
    limit: 48,
    enabled,
  });

  const devices = useMemo(
    () => query.data?.pages?.flatMap(page => page.devices || []) || [],
    [query.data],
  );

  const [sentryRef] = useInfiniteScroll({
    loading: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    disabled: false,
    onLoadMore: () => {
      if (query.hasNextPage) {
        query.fetchNextPage();
      }
    },
    rootMargin: '0px 0px 400px 0px',
  });

  return {
    devices,
    total: query.data?.pages?.[0]?.totalCount || 0,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    sentryRef,
  };
};

const getSectionColor = healthStatus =>
  healthStatus === HEALTH_STATUS.Offline ? '#ff8989' : '#ffa726';

const ClientHealthIssuesModal = ({
  open,
  onClose,
  tagId,
  selectedBucket = null,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const exportDevicesMutation = useExportDashboardClientHealthDevices();
  const archiveOfflineDevicesMutation = useArchiveDashboardOfflineDevices();
  const bulkUpdateStatusMutation = useBulkUpdateDeviceStatus();
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

  const visibleHealthStatuses = useMemo(
    () =>
      selectedBucket
        ? [selectedBucket]
        : [HEALTH_STATUS.ConfigIssue, HEALTH_STATUS.Offline],
    [selectedBucket],
  );

  const showConfigIssueSection = visibleHealthStatuses.includes(HEALTH_STATUS.ConfigIssue);
  const showOfflineSection = visibleHealthStatuses.includes(HEALTH_STATUS.Offline);

  const configIssueQuery = useHealthBucketDevices({
    enabled: open && showConfigIssueSection,
    tagId,
    healthStatus: HEALTH_STATUS.ConfigIssue,
  });
  const offlineQuery = useHealthBucketDevices({
    enabled: open && showOfflineSection,
    tagId,
    healthStatus: HEALTH_STATUS.Offline,
  });

  useEffect(() => {
    setSelectedDevices(new Set());
    setShowArchiveConfirm(false);
  }, [open, selectedBucket]);

  const isLoadingDevices =
    (showConfigIssueSection && configIssueQuery.isLoading)
    || (showOfflineSection && offlineQuery.isLoading);

  const hasVisibleDevices =
    (showConfigIssueSection && configIssueQuery.total > 0)
    || (showOfflineSection && offlineQuery.total > 0);

  const modalTitle = useMemo(() => {
    if (selectedBucket === HEALTH_STATUS.ConfigIssue) {
      return 'Configuration Issues Detected';
    }

    if (selectedBucket === HEALTH_STATUS.Offline) {
      return 'Offline Devices Detected';
    }

    return 'Client Health Issues Detected';
  }, [selectedBucket]);

  const handleDeviceToggle = deviceId => {
    const next = new Set(selectedDevices);
    if (next.has(deviceId)) {
      next.delete(deviceId);
    } else {
      next.add(deviceId);
    }
    setSelectedDevices(next);
  };

  const handleArchiveClick = () => {
    if (showOfflineSection && offlineQuery.total > 0) {
      setShowArchiveConfirm(true);
    }
  };

  const handleConfirmArchive = async () => {
    try {
      if (selectedDevices.size > 0) {
        await bulkUpdateStatusMutation.mutateAsync({
          deviceIds: Array.from(selectedDevices),
          status: DEVICE_STATUS.Archived,
        });
      } else {
        await archiveOfflineDevicesMutation.mutateAsync({
          tagId,
        });
      }

      enqueueSnackbar('Devices archived successfully.', {
        variant: 'success',
        autoHideDuration: 3000,
      });
      setShowArchiveConfirm(false);
      setSelectedDevices(new Set());
      onClose();
    } catch (_error) {
      enqueueSnackbar('Could not archive devices.', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    }
  };

  const handleCloseModal = () => {
    setSelectedDevices(new Set());
    setShowArchiveConfirm(false);
    onClose();
  };

  const handleDeviceClick = device => {
    if (!device.id) {
      return;
    }

    const currentPath = window.location.pathname;
    sessionStorage.setItem(DEVICES_PREVIOUS_PATH, currentPath);
    navigate(`${PORTAL_ROUTE}/devices/${device.id}/general`);
    onClose();
  };

  const activeExportQuery = useMemo(
    () => ({
      ...(tagId ? { TagId: tagId } : {}),
      HealthStatuses: visibleHealthStatuses,
    }),
    [tagId, visibleHealthStatuses],
  );

  const activeDevicesPageQuery = useMemo(
    () => ({
      ...(tagId ? { TagId: tagId } : {}),
      Statuses: ACTIVE_DEVICE_STATUSES,
      HealthStatuses: visibleHealthStatuses,
    }),
    [tagId, visibleHealthStatuses],
  );

  const handleExport = async () => {
    try {
      const response = await exportDevicesMutation.mutateAsync({ query: activeExportQuery });
      const blob =
        response instanceof Blob ? response : new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'client-health-devices.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (_error) {}
  };

  const handleOpenInDevices = () => {
    navigate(`${PORTAL_ROUTE}/devices?${objectToQueryString(activeDevicesPageQuery)}`);
    onClose();
  };

  const isArchiving =
    bulkUpdateStatusMutation.isLoading || archiveOfflineDevicesMutation.isLoading;

  if (showArchiveConfirm) {
    return (
      <StyledDialog open={open} onClose={handleCloseModal}>
        <StyledDialogTitle>Confirm Archive</StyledDialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: 'center', mb: 1 }}>
            {selectedDevices.size > 0
              ? `Are you sure you want to archive ${selectedDevices.size} selected device(s)?`
              : `Are you sure you want to archive all ${offlineQuery.total} offline device(s)?`}
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#ffa726', fontSize: '14px' }}>
            Archived devices will be removed from active monitoring.
          </Typography>
        </DialogContent>
        <StyledDialogActions>
          <GoBackButton onClick={() => setShowArchiveConfirm(false)}>Cancel</GoBackButton>
          <Button
            onClick={handleConfirmArchive}
            variant="outlined"
            disabled={isArchiving}>
            {isArchiving ? 'Archiving...' : 'Archive Devices'}
          </Button>
        </StyledDialogActions>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog open={open} onClose={handleCloseModal} maxWidth="md">
      <StyledDialogTitle>{modalTitle}</StyledDialogTitle>

      <DialogContent
        sx={{ maxHeight: '90vh', overflowY: 'auto', paddingX: 2, paddingTop: 1, paddingBottom: 0 }}>
        {isLoadingDevices && (
          <Stack width="100%" justifyContent="center" alignItems="center" minHeight="200px">
            <Loader />
            <Typography sx={{ mt: 1, color: '#ccc' }}>Loading device information...</Typography>
          </Stack>
        )}

        {!isLoadingDevices && (
          <Stack gap={0.5}>
            {!selectedBucket && hasVisibleDevices && (
              <>
                <ProblemDescription>
                  We&apos;ve detected {configIssueQuery.total + offlineQuery.total} devices with
                  client health issues that need your attention.
                </ProblemDescription>
              </>
            )}

            {showConfigIssueSection && (
              <Stack gap={0.5}>
                <SectionTitle color={getSectionColor(HEALTH_STATUS.ConfigIssue)}>
                  Configuration Issues ({configIssueQuery.total || configIssueQuery.devices.length})
                </SectionTitle>
                <ProblemDescription>
                  These devices are communicating but their WDAC heartbeat is outside the healthy
                  threshold.
                </ProblemDescription>
                <SolutionText>
                  Verify the heartbeat path and WDAC heartbeat configuration on these devices.
                </SolutionText>
                <Stack gap={0.5} sx={{ maxHeight: '248px', overflowY: 'auto' }}>
                  {configIssueQuery.devices.map((device, index) => (
                    <DeviceItem
                      key={device.id || index}
                      borderColor={getSectionColor(HEALTH_STATUS.ConfigIssue)}
                      onClick={() => handleDeviceClick(device)}
                      ref={
                        index === configIssueQuery.devices.length - 1
                          ? configIssueQuery.sentryRef
                          : null
                      }>
                      <DeviceName>{device.name}</DeviceName>
                      <DeviceInfo>
                        Last Contact: {device.lastContactAt ? formatDate(device.lastContactAt) : 'Never'}
                      </DeviceInfo>
                      {device.lastHeartbeatAt && (
                        <DeviceInfo>
                          Last Heartbeat: {formatDate(device.lastHeartbeatAt)}
                        </DeviceInfo>
                      )}
                    </DeviceItem>
                  ))}
                  {configIssueQuery.isFetchingNextPage && (
                    <Stack width="100%" justifyContent="center" alignItems="center" py={2}>
                      <Typography sx={{ mt: 1, color: '#ccc', fontSize: '12px' }}>
                        Loading more devices...
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}

            {showConfigIssueSection && showOfflineSection && configIssueQuery.total > 0 && offlineQuery.total > 0 && (
              <Divider sx={{ my: 0.5, backgroundColor: '#666' }} />
            )}

            {showOfflineSection && (
              <Stack gap={0.5}>
                <SectionTitle color={getSectionColor(HEALTH_STATUS.Offline)}>
                  Offline Devices ({offlineQuery.total || offlineQuery.devices.length})
                </SectionTitle>
                <ProblemDescription>
                  These devices have not contacted the service within the healthy threshold.
                </ProblemDescription>
                <SolutionText>
                  Verify device status with your IT team and archive devices that are out of
                  service.
                </SolutionText>

                <Stack gap={0.5} sx={{ maxHeight: '248px', overflowY: 'auto' }}>
                  {offlineQuery.devices.map((device, index) => (
                    <DeviceItem
                      key={device.id || index}
                      borderColor={getSectionColor(HEALTH_STATUS.Offline)}
                      onClick={() => handleDeviceClick(device)}
                      ref={index === offlineQuery.devices.length - 1 ? offlineQuery.sentryRef : null}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Checkbox
                        checked={selectedDevices.has(device.id)}
                        onChange={event => {
                          event.stopPropagation();
                          handleDeviceToggle(device.id);
                        }}
                        onClick={event => event.stopPropagation()}
                        sx={{ color: '#fff', p: 0.5 }}
                      />
                      <Box flex={1}>
                        <DeviceName>{device.name}</DeviceName>
                        <DeviceInfo>
                          Last Contact: {device.lastContactAt ? formatDate(device.lastContactAt) : 'Never'}
                        </DeviceInfo>
                        {device.lastHeartbeatAt && (
                          <DeviceInfo>
                            Last Heartbeat: {formatDate(device.lastHeartbeatAt)}
                          </DeviceInfo>
                        )}
                      </Box>
                    </Stack>
                    </DeviceItem>
                  ))}
                  {offlineQuery.isFetchingNextPage && (
                    <Stack width="100%" justifyContent="center" alignItems="center" py={2}>
                      <Typography sx={{ mt: 1, color: '#ccc', fontSize: '12px' }}>
                        Loading more devices...
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            )}

            {!hasVisibleDevices && (
              <Typography sx={{ textAlign: 'center', color: '#ccc', py: 4 }}>
                No client health issues detected.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>

      <StyledDialogActions>
        {showOfflineSection && offlineQuery.total > 0 && (
          <Button onClick={handleArchiveClick} variant="outlined">
            {selectedDevices.size > 0
              ? `Archive Selected Devices (${selectedDevices.size})`
              : 'Archive Offline Devices'}
          </Button>
        )}
        <Button
          onClick={handleExport}
          variant="outlined"
          disabled={exportDevicesMutation.isLoading || !hasVisibleDevices}>
          {exportDevicesMutation.isLoading ? 'Exporting...' : 'Export All Devices'}
        </Button>
        <Button onClick={handleOpenInDevices} variant="outlined" disabled={!hasVisibleDevices}>
          Open in Devices
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default memo(ClientHealthIssuesModal);
